import { useState, useRef, useEffect } from "react";
import { Plus } from "lucide-react";

/**
 * Polished accordion with smooth height transition + animated plus/minus icon
 * + orange gradient border highlight on the active item.
 *
 * items: [{ q: string, a: string|ReactNode }]
 * testIdPrefix: used to build `${prefix}-item-N`, `${prefix}-toggle-N`, `${prefix}-answer-N`
 * defaultOpen: index of item open on mount (or -1)
 */
export default function Accordion({ items, testIdPrefix, defaultOpen = 0 }) {
  const [openIdx, setOpenIdx] = useState(defaultOpen);

  return (
    <div className="space-y-3">
      {items.map((item, i) => (
        <AccordionItem
          key={i}
          item={item}
          index={i}
          isOpen={openIdx === i}
          onToggle={() => setOpenIdx(openIdx === i ? -1 : i)}
          testIdPrefix={testIdPrefix}
        />
      ))}
    </div>
  );
}

function AccordionItem({ item, index, isOpen, onToggle, testIdPrefix }) {
  const innerRef = useRef(null);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    if (innerRef.current) setHeight(innerRef.current.scrollHeight);
  }, [item.a, isOpen]);

  return (
    <div
      data-testid={`${testIdPrefix}-item-${index}`}
      className={`group relative rounded-2xl border bg-[#121212]/80 backdrop-blur-sm overflow-hidden transition-all duration-300 ${
        isOpen
          ? "border-[#FE7200]/50 shadow-[0_20px_50px_-20px_rgba(254,114,0,0.4)]"
          : "border-white/10 hover:border-white/25"
      }`}
    >
      {/* subtle gradient sheen when active */}
      <div
        aria-hidden
        className={`pointer-events-none absolute inset-0 bg-gradient-to-r from-[#FE7200]/0 via-[#FE7200]/[0.06] to-[#FE7200]/0 transition-opacity duration-500 ${
          isOpen ? "opacity-100" : "opacity-0"
        }`}
      />

      <button
        onClick={onToggle}
        data-testid={`${testIdPrefix}-toggle-${index}`}
        className="relative w-full flex items-center justify-between gap-3 px-5 md:px-6 py-5 text-left hover:bg-white/[0.03] transition-colors"
      >
        <div className="flex items-center gap-4">
          <span
            className={`shrink-0 inline-flex items-center justify-center h-9 w-9 rounded-lg font-display font-extrabold text-sm transition-all duration-300 ${
              isOpen
                ? "bg-[#FE7200] text-black shadow-[0_0_24px_rgba(254,114,0,0.6)]"
                : "bg-white/5 text-zinc-400 group-hover:bg-white/10 group-hover:text-white"
            }`}
          >
            {String(index + 1).padStart(2, "0")}
          </span>
          <span
            className={`font-display text-base md:text-lg font-bold transition-colors duration-300 ${
              isOpen ? "text-[#FE7200]" : "text-white"
            }`}
          >
            {item.q}
          </span>
        </div>
        <span
          className={`shrink-0 inline-flex items-center justify-center h-8 w-8 rounded-full border transition-all duration-500 ${
            isOpen
              ? "bg-[#FE7200] border-[#FE7200] text-black rotate-[135deg]"
              : "bg-white/0 border-white/15 text-white group-hover:border-[#FE7200]/60 group-hover:text-[#FE7200]"
          }`}
        >
          <Plus className="h-4 w-4" />
        </span>
      </button>

      <div
        className="overflow-hidden transition-[max-height,opacity] duration-500 ease-out"
        style={{ maxHeight: isOpen ? `${height}px` : "0px", opacity: isOpen ? 1 : 0 }}
        aria-hidden={!isOpen}
      >
        <div
          ref={innerRef}
          data-testid={`${testIdPrefix}-answer-${index}`}
          className="px-5 md:px-6 pb-6 pl-[4.25rem] text-sm md:text-base text-zinc-300 leading-relaxed"
        >
          {item.a}
        </div>
      </div>
    </div>
  );
}
